import { Trash2, UserPlus } from 'lucide-react';
import React from 'react';
import { OrgProductMultiSelect } from '../../../../../components/team/OrgProductMultiSelect';
import { CustomSelect } from '../../../../../shared/components/CustomSelect';
import type { TeamMember, TeamTabContextValue } from '../../TeamTabContext';
import {
  activeProductIdsFromPerson,
  isRosterPersonJiraLinked,
  rosterCheckboxTitlesForPerson,
  rosterPersonJiraUsername,
  teamMemberRowForPerson,
} from '../../teamRosterRowHelpers';

export interface TeamRosterGridRowProps {
  person: Record<string, unknown>;
  ctx: TeamTabContextValue;
}

export const TeamRosterGridRow: React.FC<TeamRosterGridRowProps> = ({ person, ctx }) => {
  const {
    teamMembers,
    setTeamMembers,
    rosterProductToggleUserId,
    canModifyTeam,
    currentOrg,
    inviteRoleOptions,
    visibleProductsSettings,
    handleUpdateTeamMember,
    handleDeleteTeamMember,
    persistPersonProductIds,
    openRosterInviteForPerson,
    availableRoles,
  } = ctx;

  const pidStr =
    typeof person.id === 'string' ? person.id : person.id != null ? String(person.id) : '';
  const member = teamMemberRowForPerson(teamMembers, person);
  const linkedRaw =
    person.linked_user_id ?? (person as { linkedUserId?: string | null }).linkedUserId;
  const hasLinked = linkedRaw != null && String(linkedRaw).trim() !== '';
  const inviteFromRowEligible =
    currentOrg?.role === 'owner' &&
    inviteRoleOptions.length > 0 &&
    !hasLinked &&
    pidStr.trim() !== '';
  const personTitles = rosterCheckboxTitlesForPerson(person);
  const personDisplayName =
    typeof person.display_name === 'string' ? person.display_name.trim() : '';
  const jiraAccountFieldValue = rosterPersonJiraUsername(person, member);
  const jiraLinked = isRosterPersonJiraLinked(person, member);

  const isMuted = Boolean(member && member.includeInCapacity === false);
  const nameValue = member?.name || personDisplayName;
  const emailValue = member?.email ?? (typeof person.email === 'string' ? person.email : '');

  const saveMemberOnBlur = (m: TeamMember) => {
    void handleUpdateTeamMember(m).catch((e) => {
      console.error('Error updating team member:', e);
      alert(e instanceof Error ? e.message : 'Failed to update team member');
    });
  };

  return (
    <div className={`team-roster-table-grid team-roster-table-row${isMuted ? ' is-muted' : ''}`}>
      <div className="team-roster-table-cell team-roster-table-cell--sticky-products">
        <OrgProductMultiSelect
          compact
          products={visibleProductsSettings}
          selectedIds={activeProductIdsFromPerson(person)}
          onChange={(ids) => {
            if (pidStr.trim()) void persistPersonProductIds(person, ids);
          }}
          disabled={!canModifyTeam || !pidStr.trim() || rosterProductToggleUserId === pidStr}
          checkboxTitleByProductId={personTitles}
        />
      </div>
      <div
        className="team-roster-table-cell"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
        title={
          member && (member.includeInCapacity === true || member.includeInCapacity === undefined)
            ? 'Included in capacity calculation'
            : 'Excluded from capacity calculation (or not on this product)'
        }
      >
        <input
          type="checkbox"
          disabled={!member}
          checked={
            member
              ? member.includeInCapacity === true || member.includeInCapacity === undefined
              : false
          }
          onChange={(e) => {
            if (!member?.id) return;
            const newValue = e.target.checked;
            setTeamMembers((prevMembers) =>
              prevMembers.map((m) =>
                m.id === member.id ? { ...m, includeInCapacity: newValue } : m
              )
            );
          }}
          style={{
            width: '18px',
            height: '18px',
            cursor: member ? 'pointer' : 'not-allowed',
          }}
        />
      </div>
      <div
        className="team-roster-table-cell"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
        title="Include in historical capacity (past releases)"
      >
        <input
          type="checkbox"
          disabled={!member}
          checked={member ? member.includeInHistoricalCapacity === true : false}
          onChange={(e) => {
            if (!member?.id) return;
            const newValue = e.target.checked;
            setTeamMembers((prevMembers) =>
              prevMembers.map((m) =>
                m.id === member.id ? { ...m, includeInHistoricalCapacity: newValue } : m
              )
            );
          }}
          style={{
            width: '18px',
            height: '18px',
            cursor: member ? 'pointer' : 'not-allowed',
          }}
        />
      </div>
      <div
        className="team-roster-table-cell"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
        title="Exclude from forecast distribution (Suggest distribution will not assign tasks to this person, even if they are not on this product or deactivated)"
      >
        <input
          type="checkbox"
          disabled={!member}
          checked={member ? member.excludeFromDistribution === true : false}
          onChange={(e) => {
            if (!member?.id) return;
            const newValue = e.target.checked;
            setTeamMembers((prevMembers) =>
              prevMembers.map((m) =>
                m.id === member.id ? { ...m, excludeFromDistribution: newValue } : m
              )
            );
          }}
          style={{
            width: '18px',
            height: '18px',
            cursor: member ? 'pointer' : 'not-allowed',
          }}
        />
      </div>
      <input
        type="date"
        className="form-input team-roster-table-cell"
        disabled={!member}
        value={member?.startDateInTeam || ''}
        onChange={(e) => {
          if (!member?.id) return;
          const v = e.target.value || null;
          setTeamMembers((prev) =>
            prev.map((m) => (m.id === member.id ? { ...m, startDateInTeam: v ?? undefined } : m))
          );
        }}
        onBlur={() => member && saveMemberOnBlur(member)}
        style={{ width: '100%', fontSize: '13px' }}
        title="Start date in team"
      />
      <input
        type="date"
        className="form-input team-roster-table-cell"
        disabled={!member}
        value={member?.endDateInTeam || ''}
        onChange={(e) => {
          if (!member?.id) return;
          const v = e.target.value || null;
          setTeamMembers((prev) =>
            prev.map((m) => (m.id === member.id ? { ...m, endDateInTeam: v ?? undefined } : m))
          );
        }}
        onBlur={() => member && saveMemberOnBlur(member)}
        style={{ width: '100%', fontSize: '13px' }}
        title="End date in team (empty = still in team)"
      />
      <CustomSelect
        disabled={!member}
        value={member?.role || 'developer'}
        options={availableRoles.map((role) => ({
          value: role,
          label:
            role.toLowerCase() === 'qa'
              ? 'QA'
              : role.toLowerCase() === 'developer' || role.toLowerCase() === 'dev'
                ? 'Dev'
                : role,
        }))}
        onChange={(val) => {
          if (!member?.id) return;
          const updated = teamMembers.map((m) => (m.id === member.id ? { ...m, role: val } : m));
          setTeamMembers(updated);
        }}
        onBlur={() => member && saveMemberOnBlur(member)}
        style={{ width: '100%', fontSize: '14px' }}
        className="team-roster-table-cell"
      />
      <input
        type="text"
        className="form-input team-roster-table-cell team-roster-table-cell--clip"
        disabled={!member}
        value={nameValue}
        title={nameValue}
        onChange={(e) => {
          if (!member?.id) return;
          const updated = teamMembers.map((m) =>
            m.id === member.id ? { ...m, name: e.target.value } : m
          );
          setTeamMembers(updated);
        }}
        onBlur={() => member && saveMemberOnBlur(member)}
        style={{ width: '100%', fontSize: '14px' }}
      />
      <input
        type="email"
        className="form-input team-roster-table-cell team-roster-table-cell--clip"
        disabled={!member}
        value={emailValue}
        title={emailValue}
        onChange={(e) => {
          if (!member?.id) return;
          const updated = teamMembers.map((m) =>
            m.id === member.id ? { ...m, email: e.target.value } : m
          );
          setTeamMembers(updated);
        }}
        onBlur={() => member && saveMemberOnBlur(member)}
        style={{ width: '100%', fontSize: '14px' }}
      />
      <div className="team-roster-table-cell team-roster-table-cell--clip team-roster-table-jira">
        <input
          type="text"
          className="form-input"
          disabled={!member}
          value={jiraAccountFieldValue}
          onChange={(e) => {
            if (!member?.id) return;
            const updated = teamMembers.map((m) =>
              m.id === member.id ? { ...m, jiraUsername: e.target.value } : m
            );
            setTeamMembers(updated);
          }}
          onBlur={() => member && saveMemberOnBlur(member)}
          style={{ width: '100%', fontSize: '14px' }}
          title={
            jiraAccountFieldValue ||
            (member ? 'Jira account ID or username' : 'No roster row on current product yet')
          }
          aria-describedby={pidStr ? `profile-roster-jira-status-${pidStr}` : undefined}
        />
        <span
          id={pidStr ? `profile-roster-jira-status-${pidStr}` : undefined}
          style={{
            fontSize: '11px',
            fontWeight: 600,
            color: jiraLinked
              ? 'var(--color-success, #059669)'
              : 'var(--color-text-muted, #9ca3af)',
            letterSpacing: '0.02em',
          }}
        >
          {jiraLinked ? 'Active' : 'Deactivated'}
        </span>
      </div>
      <input
        type="number"
        className="form-input team-roster-table-cell"
        min="0"
        max="24"
        step="0.5"
        disabled={!member}
        value={member?.capacityHoursPerDay ?? 8.0}
        onChange={(e) => {
          if (!member?.id) return;
          const updated = teamMembers.map((m) =>
            m.id === member.id
              ? {
                  ...m,
                  capacityHoursPerDay: parseFloat(e.target.value) || 8.0,
                }
              : m
          );
          setTeamMembers(updated);
        }}
        onBlur={() => member && saveMemberOnBlur(member)}
        style={{ width: '100%', fontSize: '14px' }}
      />
      <div
        className="team-roster-table-cell team-roster-table-cell--sticky-actions"
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '6px',
        }}
      >
        {inviteFromRowEligible ? (
          <button
            type="button"
            disabled={inviteRoleOptions.length === 0}
            onClick={() => openRosterInviteForPerson(person)}
            title="Invite to organization"
            aria-label="Invite to organization"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '8px',
              backgroundColor: 'transparent',
              color: '#059669',
              border: '1px solid #059669',
              borderRadius: '6px',
              cursor: inviteRoleOptions.length === 0 ? 'not-allowed' : 'pointer',
            }}
          >
            <UserPlus size={16} />
          </button>
        ) : null}
        <button
          type="button"
          disabled={!member?.id || !canModifyTeam || rosterProductToggleUserId === pidStr}
          onClick={() => (member?.id ? handleDeleteTeamMember(member.id) : undefined)}
          title="Remove from current product roster"
          aria-label="Remove from current product"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '8px',
            backgroundColor: 'transparent',
            color: '#ef4444',
            border: '1px solid #ef4444',
            borderRadius: '6px',
            cursor: !member?.id || !canModifyTeam ? 'not-allowed' : 'pointer',
            opacity: member?.id && canModifyTeam ? 1 : 0.4,
          }}
        >
          <Trash2 size={16} />
        </button>
      </div>
    </div>
  );
};
